/**
 * Music Search API Fallback Chain
 * Returns normalized results: {title, artist, album, duration, imageUrl, source, sourceId, preview}
 *
 * CORS policy:
 *   - iTunes, MusicBrainz, Discogs  → allow browser CORS  → called directly
 *   - Last.fm, Deezer               → block browser CORS   → proxied via /api/search-proxy
 *   - AllMusic, RateYourMusic, Spotify page-scraping → always block → removed (unreliable)
 */

const API_TIMEOUT = 5000;

// Route CORS-blocked APIs through the backend proxy instead of calling them directly
async function proxyFetch(url) {
  const proxyUrl = `/api/search-proxy?url=${encodeURIComponent(url)}`;
  const res = await fetch(proxyUrl, { signal: AbortSignal.timeout(API_TIMEOUT) });
  if (!res.ok) throw new Error(`Proxy ${res.status}`);
  return res.json();
}

// Normalize results to common format
function normalizeResult(data, source) {
  return {
    title: data.title || data.name || '',
    artist: data.artist || data.artists?.[0]?.name || data.author || '',
    album: data.album || data.albumName || '',
    duration: data.duration || null,
    imageUrl: data.imageUrl || data.image || data.artworkUrl || '',
    source,
    sourceId: `${source}:${data.id || data.mbid || data.key || Math.random()}`,
    preview: data.preview || data.previewUrl || ''
  };
}

// 1. Last.fm — proxied through backend (CORS blocked from browser)
async function searchLastFm(query) {
  try {
    // Use Last.fm's JSON API via the backend proxy
    const url = `https://ws.audioscrobbler.com/2.0/?method=track.search&track=${encodeURIComponent(query)}&api_key=nokey&format=json&limit=3`;
    const data = await proxyFetch(url);
    const tracks = data?.results?.trackmatches?.track || [];
    return (Array.isArray(tracks) ? tracks : [tracks]).slice(0, 3).map(t => normalizeResult({
      title: t.name,
      artist: t.artist,
      imageUrl: t.image?.find(i => i.size === 'large')?.['#text'] || '',
      id: `lastfm_${t.mbid || t.name}`
    }, 'lastfm'));
  } catch (err) {
    return [];
  }
}

// 2. MusicBrainz
async function searchMusicBrainz(query) {
  try {
    const url = `https://musicbrainz.org/ws/2/recording?query=${encodeURIComponent(query)}&fmt=json&limit=3`;
    const res = await fetch(url, {
      signal: AbortSignal.timeout(API_TIMEOUT),
      headers: { 'User-Agent': 'DJ-Pro-Studio/1.0' }
    });
    const data = await res.json();

    return (data.recordings || []).map(r => ({
      title: r.title,
      artist: r['artist-credit']?.[0]?.artist?.name || '',
      duration: r.length ? Math.round(r.length / 1000) : null,
      id: r.id,
      mbid: r.id
    })).map(d => normalizeResult(d, 'musicbrainz'));
  } catch (err) {
    return [];
  }
}

// 3. Discogs
async function searchDiscogs(query) {
  try {
    const url = `https://api.discogs.com/database/search?q=${encodeURIComponent(query)}&type=release&per_page=3`;
    const res = await fetch(url, {
      signal: AbortSignal.timeout(API_TIMEOUT),
      headers: { 'User-Agent': 'DJ-Pro-Studio/1.0' }
    });
    const data = await res.json();

    return (data.results || []).map(r => ({
      title: r.title,
      artist: r.basic_information?.artists?.[0]?.name || '',
      imageUrl: r.basic_information?.thumb || r.cover_image || '',
      id: r.id,
      year: r.year
    })).map(d => normalizeResult(d, 'discogs'));
  } catch (err) {
    return [];
  }
}

// 4. Genius (song search)
async function searchGenius(query) {
  try {
    const url = `https://genius.com/api/search/multi?q=${encodeURIComponent(query)}`;
    const res = await fetch(url, { signal: AbortSignal.timeout(API_TIMEOUT) });
    const data = await res.json();

    const hits = data.response?.sections?.[0]?.hits || [];
    return hits.slice(0, 3).map(h => {
      const song = h.result;
      return {
        title: song.title,
        artist: song.primary_artist?.name || '',
        imageUrl: song.song_art_image_url || '',
        id: song.id
      };
    }).map(d => normalizeResult(d, 'genius'));
  } catch (err) {
    return [];
  }
}

// 5. Deezer — proxied through backend (CORS blocked from browser)
async function searchDeezer(query) {
  try {
    const url = `https://api.deezer.com/search?q=${encodeURIComponent(query)}&limit=3`;
    const data = await proxyFetch(url);

    return (data.data || []).map(t => ({
      title: t.title,
      artist: t.artist?.name || '',
      album: t.album?.title || '',
      duration: t.duration,
      imageUrl: t.album?.cover_big || t.album?.cover || '',
      preview: t.preview,
      id: t.id
    })).map(d => normalizeResult(d, 'deezer'));
  } catch (err) {
    return [];
  }
}

// 6. iTunes API (already proven working)
async function searchItunes(query) {
  try {
    const url = `https://itunes.apple.com/search?term=${encodeURIComponent(query)}&media=music&entity=song&limit=3`;
    const res = await fetch(url, { signal: AbortSignal.timeout(API_TIMEOUT) });
    const data = await res.json();

    return (data.results || []).map(r => ({
      title: r.trackName,
      artist: r.artistName,
      album: r.collectionName,
      duration: r.trackTimeMillis ? Math.round(r.trackTimeMillis / 1000) : null,
      imageUrl: r.artworkUrl100?.replace('100x100', '300x300') || '',
      preview: r.previewUrl,
      id: r.trackId
    })).map(d => normalizeResult(d, 'itunes'));
  } catch (err) {
    return [];
  }
}

// 7. Spotify (public search - no auth required for search)
async function searchSpotify(query) {
  try {
    const url = `https://open.spotify.com/search/${encodeURIComponent(query)}/tracks`;
    const res = await fetch(url, { signal: AbortSignal.timeout(API_TIMEOUT) });
    const html = await res.text();

    // Extract JSON from page (minimal parsing)
    const jsonMatch = html.match(/"name":"([^"]+)".*?"artists":\[{"name":"([^"]+)"/g) || [];
    const results = [];

    for (let i = 0; i < Math.min(3, jsonMatch.length); i++) {
      const parts = jsonMatch[i].match(/"name":"([^"]+)".*?"artists":\[{"name":"([^"]+)"/);
      if (parts) {
        results.push({
          title: parts[1],
          artist: parts[2],
          id: `spotify_${i}`
        });
      }
    }
    return results.map(r => normalizeResult(r, 'spotify'));
  } catch (err) {
    return [];
  }
}

// 8. AcousticBrainz (open music database)
async function searchAcousticBrainz(query) {
  try {
    const url = `https://acousticbrainz.org/api/v1/search?query=${encodeURIComponent(query)}`;
    const res = await fetch(url, { signal: AbortSignal.timeout(API_TIMEOUT) });
    const data = await res.json();

    return (data.results || []).slice(0, 3).map(r => ({
      title: r.recording?.title || '',
      artist: r.recording?.artists?.[0]?.name || '',
      id: r.recording?.mbid || r.id,
      mbid: r.recording?.mbid
    })).map(d => normalizeResult(d, 'acousticbrainz'));
  } catch (err) {
    return [];
  }
}

// 9. AllMusic (reference database via ethical scraping)
async function searchAllMusic(query) {
  try {
    const url = `https://www.allmusic.com/search/songs/${encodeURIComponent(query)}`;
    const res = await fetch(url, {
      signal: AbortSignal.timeout(API_TIMEOUT),
      headers: { 'User-Agent': 'Mozilla/5.0' }
    });
    const html = await res.text();

    // Extract song/artist pairs
    const matches = html.match(/<h3[^>]*>([^<]+)<\/h3>[\s\S]*?<h4[^>]*>([^<]+)<\/h4>/g) || [];
    const results = [];

    for (let i = 0; i < Math.min(3, matches.length); i++) {
      const parts = matches[i].match(/<h3[^>]*>([^<]+)<\/h3>[\s\S]*?<h4[^>]*>([^<]+)<\/h4>/);
      if (parts) {
        results.push({
          title: parts[1].trim(),
          artist: parts[2].trim(),
          id: `allmusic_${i}`
        });
      }
    }
    return results.map(r => normalizeResult(r, 'allmusic'));
  } catch (err) {
    return [];
  }
}

// 10. RateYourMusic (community music database)
async function searchRateYourMusic(query) {
  try {
    const url = `https://rateyourmusic.com/search?searchtype=l&searchquery=${encodeURIComponent(query)}&search_type=song`;
    const res = await fetch(url, {
      signal: AbortSignal.timeout(API_TIMEOUT),
      headers: { 'User-Agent': 'Mozilla/5.0' }
    });
    const html = await res.text();

    // Extract song entries (minimal parsing)
    const matches = html.match(/<a href="\/f\/[^"]+"[^>]*>([^<]+)<\/a>[\s\S]*?<em>([^<]+)<\/em>/g) || [];
    const results = [];

    for (let i = 0; i < Math.min(3, matches.length); i += 2) {
      if (matches[i] && matches[i + 1]) {
        results.push({
          title: matches[i].replace(/<[^>]+>/g, '').trim(),
          artist: matches[i + 1].replace(/<[^>]+>/g, '').trim(),
          id: `rym_${i}`
        });
      }
    }
    return results.map(r => normalizeResult(r, 'rateyourmusic'));
  } catch (err) {
    return [];
  }
}

/**
 * Main search function - fallback chain
 * Returns first successful result set (up to 3 tracks)
 */
export async function searchMusic(query) {
  if (!query || query.trim().length < 2) return [];

  const apis = [
    // CORS-safe (direct browser calls) — fastest, most reliable
    { name: 'iTunes',        fn: searchItunes },        // proven working ✓
    { name: 'MusicBrainz',  fn: searchMusicBrainz },   // CORS-friendly
    { name: 'Discogs',      fn: searchDiscogs },        // CORS-friendly
    { name: 'Genius',       fn: searchGenius },         // usually CORS-friendly
    // Proxied through backend (CORS-blocked from browser)
    { name: 'Deezer',       fn: searchDeezer },         // via /api/search-proxy
    { name: 'LastFm',       fn: searchLastFm },         // via /api/search-proxy
    // AcousticBrainz as last resort
    { name: 'AcousticBrainz', fn: searchAcousticBrainz },
    // NOTE: AllMusic, RateYourMusic, Spotify page-scraping removed —
    // all block CORS and rely on fragile HTML parsing that breaks on any site update
  ];

  for (const api of apis) {
    try {
      const results = await api.fn(query);
      if (results.length > 0) {
        console.log(`✓ ${api.name} returned ${results.length} results`);
        return results.slice(0, 3); // Return max 3
      }
    } catch (err) {
      console.warn(`✗ ${api.name} failed:`, err.message);
    }
  }

  return []; // All APIs failed
}

export default searchMusic;
